import { useEffect, useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button, CodeSearchField, Modal } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useServiceFeesSearch } from '@/settings/services';
import { cn } from '@/lib/utils';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import type {
  BillingProfile,
  BillingProfileService,
  ProviderBillingVisitFee,
  ServiceFeesSearchItem,
} from '@/types/settings-api';

/**
 * Billing Profile edit dialog — PROD-763 (PR-B + PR-C).
 *
 * Flutter parity: `edit_billing_profile_widget.dart` +
 * `service_code_list_widget.dart`. Complete edit surface:
 *
 *   - Profile name input
 *   - Add Service via `useServiceFeesSearch` typeahead (PR-C)
 *   - Mode toggle: Provider Fees / All (Flutter `isProviderFeeSearch`,
 *     dart:236-277)
 *   - Service list display (procedureCode + per-location fees,
 *     read-only — fees are sourced from the org catalog at pick time,
 *     they're not editable in this surface; per Flutter
 *     `service_code_list_widget.dart:86-91`, fees are rendered as
 *     plain Text, not inputs)
 *   - Per-service Delete (PR-C):
 *       * `id: null` (newly-added) → filter out of array
 *       * `id: GUID` (existing) → set `isDeleted: true`, keep in array
 *         so the backend marks it archived on save
 *   - Save / Cancel + Archive (profile-level) with two-tap gate
 *
 * Wire-shape invariants preserved (verified against live save cURL
 * 2026-05-15):
 *   - New `BillingProfileService.id` and `visitFees[].id` /
 *     `visitFees[].serviceId` ship as `null`; backend allocates GUIDs
 *     server-side. `JSON.stringify` preserves null per the invariant
 *     proved across every PROD-761 typeahead.
 *   - `visitFees[]` for a newly-picked service are mapped from the
 *     search-result's `fees[]` array: each `ServiceFeesSearchFee`
 *     becomes a `ProviderBillingVisitFee` with the same `location`,
 *     `fee`, and fresh `id: null` + `serviceId: null` (Flutter
 *     `edit_billing_profile_widget.dart:208-212`).
 *   - Profile-level `isUpdated: true` flips on edit save (Flutter
 *     convention — backend discriminator alongside `id` non-null).
 *
 * Parent dispatches the save mutation. Dialog returns the modified
 * `BillingProfile` via `onSave(next)` — same contract as PR-B.
 */
export interface BillingProfileEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: BillingProfile;
  onSave: (next: BillingProfile) => void;
  onArchive?: () => void;
}

export function BillingProfileEditDialog({
  open,
  onOpenChange,
  initial,
  onSave,
  onArchive,
}: BillingProfileEditDialogProps) {
  const isEdit = !!initial.id;
  const [draft, setDraft] = useState<BillingProfile>(initial);
  const [confirmingArchive, setConfirmingArchive] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(initial);
      setConfirmingArchive(false);
    }
  }, [open, initial]);

  const isValid = draft.name.trim().length > 0;

  const handleSave = () => {
    if (!isValid) {
      toast.error('Profile name is required.');
      return;
    }
    onSave({
      ...draft,
      isUpdated: isEdit ? true : false,
    });
    onOpenChange(false);
  };

  const handleAddService = (item: ServiceFeesSearchItem) => {
    // Duplicate guard — match Flutter convention (services are uniquely
    // keyed by `procedureCode.code`; the same code shouldn't appear
    // twice in a profile). Compare against the visible (non-deleted)
    // service set; soft-deleted entries can still ride the array.
    const alreadyAttached = draft.services.some(
      (s) => !s.isDeleted && s.procedureCode?.code === item.procedureCode?.code,
    );
    if (alreadyAttached) {
      toast.error('That service is already in this profile.');
      return;
    }
    // Map ServiceFeesSearchItem → BillingProfileService.
    // Matches Flutter `edit_billing_profile_widget.dart:202-213`:
    //   selectedItem.fees.map(e => VisitFee.fromJson(e.toJson())) →
    //   the search result's `fees[]` becomes the new service's
    //   `visitFees[]`. All IDs reset to null (backend allocates).
    const newService: BillingProfileService = {
      id: null,
      procedureCode: item.procedureCode,
      visitFees: item.fees.map<ProviderBillingVisitFee>((f) => ({
        id: null,
        location: f.location,
        fee: f.fee,
        serviceId: null,
        isArchived: false,
      })),
      shortDescription: item.shortDescription,
      isDeleted: null,
      billingGroupId: null,
    };
    setDraft({
      ...draft,
      services: [...draft.services, newService],
      isProviderFee: false,
    });
  };

  const handleRemoveService = (index: number) => {
    const target = draft.services[index];
    if (!target) return;
    if (target.id === null) {
      // Unsaved row — drop it.
      setDraft({
        ...draft,
        services: draft.services.filter((_, i) => i !== index),
      });
    } else {
      // Persisted row — flip soft-delete flag. Backend marks archived
      // on save; the row keeps the join PK so the archive completes.
      setDraft({
        ...draft,
        services: draft.services.map((s, i) =>
          i === index ? { ...s, isDeleted: true } : s,
        ),
      });
    }
  };

  // Visible services = non-soft-deleted. Soft-deleted rows still ride
  // the array (round-trip on save), they just don't render.
  const visibleServices = useMemo(
    () =>
      draft.services
        .map((service, originalIndex) => ({ service, originalIndex }))
        .filter(({ service }) => !service.isDeleted),
    [draft.services],
  );

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={isEdit ? 'Edit Billing Profile' : 'Add Billing Profile'}
      size="lg"
      footer={<><div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={handleSave}
              disabled={!isValid}
            >
              Save
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
          </div>
          {isEdit && onArchive && (
            <Button
              type="button"
              variant="tertiary"
              onClick={() => {
                if (confirmingArchive) {
                  onArchive();
                  onOpenChange(false);
                } else {
                  setConfirmingArchive(true);
                }
              }}
              className={
                confirmingArchive
                  ? 'bg-status-error text-text-inverse hover:bg-status-error/90'
                  : 'text-status-error hover:text-status-error hover:bg-status-error/10'
              }
              title={
                confirmingArchive
                  ? undefined
                  : 'Previously created invoices using this profile will not be affected.'
              }
            >
              {confirmingArchive ? 'Confirm Archive' : 'Archive'}
            </Button>
          )}</>}
      footerAlign="between"
    >
        

        <div className="space-y-6 py-2 max-h-overlay-body-compact overflow-y-auto scrollbar-hide">
          <div>
            <FieldLabel stacked>
              Profile name <span className="text-status-error">*</span>
            </FieldLabel>
            <TextField
              value={draft.name}
              placeholder="e.g. Standard, Couple Session, Insurance Plan"
              maxLength={120}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </div>

          <div className="border-t border-border-hairline pt-4">
            <FieldLabel stacked>Services</FieldLabel>
            <ServicePicker
              providerId={draft.providerId}
              onAdd={handleAddService}
            />
            <p className="mt-2 text-size-sm font-normal text-text-secondary">
              Search Provider Fees to add codes to this billing profile.
            </p>

            <div className="mt-4">
              <ServiceList
                services={visibleServices}
                onRemove={handleRemoveService}
              />
            </div>
          </div>
        </div>

        
      </Modal>
  );
}

/**
 * Service typeahead — wraps `useServiceFeesSearch` with the
 * Provider-Fees / All mode toggle (Flutter `isProviderFeeSearch`).
 *
 * Same SearchPicker shape used across every typeahead in the
 * practitioner-edit surfaces: debounced 250ms, AbortSignal forwarded
 * via React Query's signal, duplicate guard handled by the parent's
 * `onAdd` which checks `procedureCode.code` against existing
 * non-soft-deleted services.
 */
function ServicePicker({
  providerId,
  onAdd,
}: {
  providerId: string;
  onAdd: (item: ServiceFeesSearchItem) => void;
}) {
  const [mode, setMode] = useState<'provider' | 'all'>('all');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 250);

  const { data, isLoading } = useServiceFeesSearch({
    providerId,
    q: debouncedSearch,
    type: mode,
  });

  const results = data?.results ?? [];

  const options = useMemo(
    () =>
      results.map((r) => ({
        id: r.id,
        label:
          r.procedureCode?.displayName ??
          r.procedureCode?.code ??
          r.shortDescription ??
          r.id,
        secondary: r.procedureCode?.code ?? undefined,
      })),
    [results],
  );

  const handleSelect = (id: string) => {
    const found = results.find((r) => r.id === id);
    if (!found) return;
    onAdd(found);
    setSearch('');
    setOpen(false);
  };

  // The design draws the DS CodeSearchField here (Dialogs.tsx billing-add): the search field with its result rows in
  // the flow under it. Same debounced search, same client-side filter as the old SearchPicker, same pick handler.
  const q = search.trim().toLowerCase();
  const shown = q
    ? options.filter(
        (o) => o.label.toLowerCase().includes(q) || (o.secondary ?? '').toLowerCase().includes(q),
      )
    : [];

  return (
    <div data-slot="billing-service-picker" className="space-y-2">
      <CodeSearchField
        value={search}
        placeholder="Search for Provider Fees"
        onChange={(next) => {
          setSearch(next);
          setOpen(true);
        }}
        loading={open && isLoading}
        results={open ? shown.map((o) => ({ id: o.id, display: o.label, code: o.secondary })) : []}
        onResultClick={(r) => handleSelect(r.id)}
      />

      <div data-slot="billing-mode-row" className="flex items-center gap-4 text-sm font-semibold">
        <button
          type="button"
          aria-pressed={mode === 'provider'}
          onClick={() => setMode('provider')}
          className={cn(
            'transition-colors',
            mode === 'provider'
              ? 'text-brand-primary'
              : 'text-text-hint hover:text-text-muted',
          )}
        >
          Provider Fees
        </button>
        <button
          type="button"
          aria-pressed={mode === 'all'}
          onClick={() => setMode('all')}
          className={cn(
            'transition-colors',
            mode === 'all'
              ? 'text-brand-primary'
              : 'text-text-hint hover:text-text-muted',
          )}
        >
          All
        </button>
      </div>
    </div>
  );
}

/**
 * Service list display — read-only fees per location, one row per
 * service with a Delete button.
 *
 * Flutter renders this as a custom horizontal table
 * (`service_code_list_widget.dart` — columns: Service / fee-per-location /
 * delete). Per the Flutter-web workaround simplification rule in
 * CLAUDE.md, the React idiom for ≤30 rows is a vertical list of
 * cards; the table layout is a Flutter-canvas workaround that doesn't
 * carry forward.
 *
 * Per-service rendering:
 *   - Procedure code displayName (bold)
 *   - shortDescription (small muted) if present
 *   - System + code line (small muted) — "CPT: 26118"
 *   - Per-location fee chips: "Office: $1500 · Virtual: $300" — read
 *     only, sourced from `visitFees[]` at pick time
 *   - Delete button (right-aligned, soft trash icon)
 *
 * Per @samer PR #136 review item 3: each visit-fee row in this
 * render uses `fee.id ?? \`fee-new-${index}\`` for its React key.
 * NEVER `fee.serviceId` — that's null on new rows and would collapse
 * to duplicates.
 */
function ServiceList({
  services,
  onRemove,
}: {
  services: Array<{ service: BillingProfileService; originalIndex: number }>;
  onRemove: (originalIndex: number) => void;
}) {
  if (services.length === 0) {
    return (
      <p className="text-sm text-text-muted">
        No services in this profile yet. Use the search above to add codes.
      </p>
    );
  }

  return (
    <ul className="space-y-2 max-h-75 overflow-y-auto scrollbar-hide rounded-md border border-border-hairline bg-surface-card">
      {services.map(({ service, originalIndex }) => (
        <li
          key={service.id ?? `svc-new-${originalIndex}`}
          className="flex items-start justify-between gap-2 px-3 py-2.5 border-b border-border-hairline last:border-b-0"
        >
          <div className="flex-1">
            <p className="text-sm font-semibold text-text-default">
              {service.procedureCode?.displayName ||
                service.procedureCode?.code ||
                'Service'}
            </p>
            {service.shortDescription &&
              service.shortDescription !==
                service.procedureCode?.displayName && (
                <p className="mt-1 text-xs text-text-muted line-clamp-2">
                  {service.shortDescription}
                </p>
              )}
            {service.procedureCode?.code && (
              <p className="mt-1 text-xs text-text-hint">
                {service.procedureCode.system ?? 'CODE'}:{' '}
                {service.procedureCode.code}
              </p>
            )}

            {service.visitFees && service.visitFees.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                {service.visitFees
                  .filter((f) => !f.isArchived)
                  .map((fee, feeIdx) => (
                    <span
                      key={fee.id ?? `fee-new-${originalIndex}-${feeIdx}`}
                      className="text-sm text-text-default"
                    >
                      <span className="text-text-muted">
                        {fee.location?.name ?? 'Location'}:
                      </span>{' '}
                      <span className="font-semibold">
                        {formatFee(fee.fee)}
                      </span>
                    </span>
                  ))}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => onRemove(originalIndex)}
            aria-label={`Remove ${
              service.procedureCode?.displayName || 'service'
            }`}
            className="icon-action flex-shrink-0"
          >
            <Trash2 className="size-4" aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Format a numeric fee as a display string. Currency code is sourced
 * from `OrgBillingSettings.currency` on Flutter, but the React
 * surfaces don't thread that into this dialog yet. Show the bare
 * number for now (matches Flutter behavior when currency code is null
 * — `currency?.code ?? "..."`).
 */
function formatFee(fee: number | null | undefined): string {
  if (fee == null) return '—';
  if (Number.isInteger(fee)) return fee.toString();
  return fee.toFixed(2);
}

